import * as React from 'react';
import { z } from 'zod/v4';
import {
  AddressAutocompleteInput,
  type AddressInputFieldErrors,
  type AddressInputLabels,
  type AddressInputPlaceholders,
  type AddressSuggestion,
  type AddressSuggestionsProvider,
  type AddressDetailsResolver,
  type AddressValue,
} from './address-autocomplete-input';
import { Label } from './label';
import { Select } from './select';
import { SWISS_CANTONS, type SwissCanton } from './swiss-cantons';

export type { SwissCanton } from './swiss-cantons';

export interface SwissAddress {
  address: string;
  streetNumber: string;
  postalCode: string;
  city: string;
  canton: SwissCanton;
  country: 'CH';
  featureId: string;
  latitude: number;
  longitude: number;
  /** OFS/BFS commune number from the official building address directory. */
  communeBfs?: number;
}

export interface ManualSwissAddress {
  address: string;
  streetNumber: string;
  postalCode: string;
  city: string;
  canton?: SwissCanton;
  country: 'CH';
  featureId?: undefined;
  latitude?: undefined;
  longitude?: undefined;
  communeBfs?: undefined;
}

export interface LiechtensteinAddress {
  address: string;
  streetNumber: string;
  postalCode: string;
  city: string;
  country: 'LI';
  featureId: string;
  latitude: number;
  longitude: number;
  canton?: undefined;
  communeBfs?: undefined;
}

export interface ManualLiechtensteinAddress {
  address: string;
  streetNumber: string;
  postalCode: string;
  city: string;
  country: 'LI';
  featureId?: undefined;
  latitude?: undefined;
  longitude?: undefined;
  canton?: undefined;
  communeBfs?: undefined;
}

export type SwissAddressValue =
  | SwissAddress
  | ManualSwissAddress
  | LiechtensteinAddress
  | ManualLiechtensteinAddress;

type AddressDefaultLines = Pick<
  SwissAddress,
  'address' | 'postalCode' | 'city'
> & {
  streetNumber?: string;
  featureId?: string;
  latitude?: number;
  longitude?: number;
};

export type SwissAddressDefaultValue = AddressDefaultLines &
  (
    | {
        country?: 'CH';
        canton?: SwissCanton;
        communeBfs?: number;
      }
    | {
        country: 'LI';
        canton?: undefined;
        communeBfs?: undefined;
      }
  );

export type GeoAdminCountry = 'CH' | 'LI';

const zGeoAdminLink = z.object({
  title: z.string(),
  href: z.string(),
  rel: z.string().optional(),
});

type GeoAdminLink = z.infer<typeof zGeoAdminLink>;

const GEOADMIN_BASE_URL = 'https://api3.geo.admin.ch';
const OFFICIAL_ADDRESS_DIRECTORY =
  'ch.swisstopo.amtliches-gebaeudeadressverzeichnis';
const LIECHTENSTEIN_COMMUNE_MIN = 7001;
const LIECHTENSTEIN_COMMUNE_MAX = 7011;

type SwissDefaultValue = SwissAddressDefaultValue;

interface GeoAdminSearchResult {
  attrs: {
    featureId: string;
    links?: GeoAdminLink[];
  };
  links?: GeoAdminLink[];
}

function isLiechtensteinCommune(communeBfs: number): boolean {
  return (
    communeBfs >= LIECHTENSTEIN_COMMUNE_MIN &&
    communeBfs <= LIECHTENSTEIN_COMMUNE_MAX
  );
}

function countryFromSearchDetail(detail?: string): GeoAdminCountry {
  return detail && /\b(?:700[1-9]|701[01])\b/.test(detail) ? 'LI' : 'CH';
}

function officialAddressUrl(result: GeoAdminSearchResult): string | null {
  const link = [...(result.attrs.links ?? []), ...(result.links ?? [])].find(
    (candidate) => candidate.title === OFFICIAL_ADDRESS_DIRECTORY,
  );
  if (!link) return null;

  const href = link.href.replace(
    '<featureid>',
    encodeURIComponent(result.attrs.featureId),
  );
  const url = new URL(href, GEOADMIN_BASE_URL);
  if (
    url.origin !== GEOADMIN_BASE_URL ||
    !url.pathname.includes(`/MapServer/${OFFICIAL_ADDRESS_DIRECTORY}/`)
  ) {
    return null;
  }
  return url.toString();
}

function parsePostalLocality(zipLabel: string): {
  postalCode: string;
  city: string;
} {
  const match = /^(\S+)\s+(.+)$/.exec(zipLabel.trim());
  if (!match) throw new Error('GeoAdmin returned an invalid postal locality.');
  return { postalCode: match[1], city: match[2] };
}

function isVerifiedAddress(value: SwissBaseAddressValue | null): boolean {
  return Boolean(
    value?.placeId &&
    value.coordinates &&
    (value.country === 'LI' || value.canton),
  );
}

export interface SwissAddressInputProps {
  name?: string;
  defaultValue?: SwissAddressDefaultValue | null;
  onChange?: (value: SwissAddressValue | null) => void;
  label?: string;
  helperText?: string;
  error?: boolean;
  errors?: AddressInputFieldErrors;
  placeholder?: string;
  noOptionsText?: string;
  searchingText?: string;
  labels?: Partial<
    AddressInputLabels & {
      canton: string;
      cantonPlaceholder: string;
      noCantons: string;
    }
  >;
  placeholders?: Partial<AddressInputPlaceholders>;
  disabled?: boolean;
  required?: boolean;
  id?: string;
  className?: string;
  allowedCantons?: readonly SwissCanton[];
  onFetchError?: (error: unknown) => void;
  /** Render dropdowns inline instead of portaling to document.body. */
  disablePortal?: boolean;
  /** Open the manual fields on first render. */
  defaultManual?: boolean;
}

const zGeoAdminResults = z.object({
  results: z.array(
    z.object({
      id: z.union([z.number(), z.string()]),
      attrs: z.object({
        label: z.string(),
        featureId: z.string(),
        detail: z.string().optional(),
        lat: z.number(),
        lon: z.number(),
        links: z.array(zGeoAdminLink).optional(),
      }),
      links: z.array(zGeoAdminLink).optional(),
    }),
  ),
});

const zGeoAdminFeature = z.object({
  feature: z.object({
    featureId: z.union([z.string(), z.number()]),
    attributes: z.object({
      adr_egaid: z.number(),
      stn_label: z.string(),
      adr_number: z.string(),
      zip_label: z.string(),
      com_fosnr: z.number(),
    }),
  }),
});

interface SwissBaseAddressValue extends AddressValue {
  country: GeoAdminCountry;
  canton?: SwissCanton;
  communeBfs?: number;
}

interface SwissAddressSuggestion extends AddressSuggestion<SwissBaseAddressValue> {
  coordinates: { lat: number; lng: number };
  canton: SwissCanton | null;
  country: GeoAdminCountry;
  detailsUrl: string;
}

const SWISS_LABELS: AddressInputLabels & {
  canton: string;
  cantonPlaceholder: string;
  noCantons: string;
} = {
  search: 'Adresse',
  manualEntry: "Saisir l'adresse manuellement",
  street: 'Rue',
  streetNumber: 'Numéro',
  postalCode: 'Code postal',
  city: 'Ville',
  country: 'Pays',
  canton: 'Canton',
  department: 'Département',
  cantonPlaceholder: 'Sélectionner un canton',
  noCantons: 'Aucun canton disponible.',
  searching: 'Recherche...',
  noOptions: 'Aucune adresse trouvée',
  results: '{count} résultats',
  countrySearch: 'Rechercher...',
  noCountries: 'Aucun pays trouvé.',
};

const SWISS_PLACEHOLDERS: AddressInputPlaceholders = {
  search: 'Rue de la Paix 1, 1000 Lausanne',
  street: 'Rue',
  streetNumber: 'Numéro',
  postalCode: 'Code postal',
  city: 'Ville',
  country: 'Suisse',
  canton: 'Sélectionner un canton',
  department: 'Sélectionner un département',
};

function formatDefaultValue(value: SwissDefaultValue): string {
  const street = [value.address, value.streetNumber].filter(Boolean).join(' ');
  return [street, `${value.postalCode} ${value.city}`].join(', ');
}

function removeTags(html: string): string {
  return html
    .replace(/<i>.*?<\/i>\s+|<\/b>/g, '')
    .replace(/^<b>/g, '')
    .replace(/\s+<b>/g, ', ');
}

function extractCanton(detail?: string): SwissCanton | null {
  const candidate = detail?.trim().split(/\s+/).at(-1)?.toUpperCase();
  return SWISS_CANTONS.includes(candidate as SwissCanton)
    ? (candidate as SwissCanton)
    : null;
}

function isAbortError(error: unknown, signal: AbortSignal): boolean {
  return (
    signal.aborted ||
    (error instanceof DOMException && error.name === 'AbortError') ||
    (error instanceof Error && error.name === 'AbortError')
  );
}

function toBaseValue(value: SwissDefaultValue): SwissBaseAddressValue {
  const country = value.country ?? 'CH';
  return {
    street: value.address,
    streetNumber: value.streetNumber ?? '',
    postalCode: value.postalCode,
    city: value.city,
    country,
    ...(country === 'CH' && value.canton ? { canton: value.canton } : {}),
    ...(value.featureId ? { placeId: value.featureId } : {}),
    ...(value.latitude !== undefined && value.longitude !== undefined
      ? {
          coordinates: {
            lat: value.latitude,
            lng: value.longitude,
          },
        }
      : {}),
    ...(country === 'CH' && value.communeBfs !== undefined
      ? { communeBfs: value.communeBfs }
      : {}),
  };
}

function toSwissValue(value: SwissBaseAddressValue): SwissAddressValue {
  if (value.country === 'LI') {
    if (value.placeId && value.coordinates) {
      return {
        address: value.street,
        streetNumber: value.streetNumber,
        postalCode: value.postalCode,
        city: value.city,
        country: 'LI',
        featureId: value.placeId,
        latitude: value.coordinates.lat,
        longitude: value.coordinates.lng,
      };
    }
    return {
      address: value.street,
      streetNumber: value.streetNumber,
      postalCode: value.postalCode,
      city: value.city,
      country: 'LI',
    };
  }

  if (value.placeId && value.coordinates && value.canton) {
    return {
      address: value.street,
      streetNumber: value.streetNumber,
      postalCode: value.postalCode,
      city: value.city,
      canton: value.canton,
      country: 'CH',
      featureId: value.placeId,
      latitude: value.coordinates.lat,
      longitude: value.coordinates.lng,
      ...(value.communeBfs !== undefined
        ? { communeBfs: value.communeBfs }
        : {}),
    };
  }

  return {
    address: value.street,
    streetNumber: value.streetNumber,
    postalCode: value.postalCode,
    city: value.city,
    ...(value.canton ? { canton: value.canton } : {}),
    country: 'CH',
  };
}

function createSwissAddressSuggestionsProvider(
  allowedCantons?: readonly SwissCanton[],
  country: GeoAdminCountry = 'CH',
): AddressSuggestionsProvider<SwissAddressSuggestion> {
  return async (query, { signal }) => {
    const parameters = new URLSearchParams({
      type: 'locations',
      origins: 'address',
      searchText: query,
    });
    const response = await fetch(
      `https://api3.geo.admin.ch/rest/services/api/SearchServer?${parameters.toString()}`,
      { signal },
    );
    if (!response.ok) {
      throw new Error(
        `GeoAdmin address search failed with HTTP ${response.status}.`,
      );
    }
    const parsed = zGeoAdminResults.parse(await response.json());
    const seen = new Set<string>();

    return parsed.results.flatMap((result) => {
      const canton = extractCanton(result.attrs.detail);
      const resultCountry = countryFromSearchDetail(result.attrs.detail);
      const detailsUrl = officialAddressUrl(result);
      if (result.attrs.label.includes('#')) return [];
      if (resultCountry !== country || !detailsUrl) return [];
      if (
        country === 'CH' &&
        allowedCantons &&
        (!canton || !allowedCantons.includes(canton))
      ) {
        return [];
      }
      if (seen.has(detailsUrl)) return [];
      seen.add(detailsUrl);
      return [
        {
          id: detailsUrl.split('/').at(-1) ?? result.attrs.featureId,
          label: removeTags(result.attrs.label),
          description: result.attrs.detail,
          coordinates: {
            lat: result.attrs.lat,
            lng: result.attrs.lon,
          },
          canton,
          country: resultCountry,
          detailsUrl,
        },
      ];
    });
  };
}

function createSwissAddressDetailsResolver(
  allowedCantons?: readonly SwissCanton[],
  country: GeoAdminCountry = 'CH',
): AddressDetailsResolver<SwissAddressSuggestion, SwissBaseAddressValue> {
  return async (suggestion, { signal }) => {
    const response = await fetch(suggestion.detailsUrl, { signal });
    if (!response.ok) {
      throw new Error(
        `GeoAdmin address details failed with HTTP ${response.status}.`,
      );
    }
    const parsed = zGeoAdminFeature.parse(await response.json());
    const attributes = parsed.feature.attributes;
    const resolvedCountry = isLiechtensteinCommune(attributes.com_fosnr)
      ? 'LI'
      : 'CH';
    if (resolvedCountry !== country || suggestion.country !== country) {
      throw new Error('The selected address is outside the selected country.');
    }
    const locality = parsePostalLocality(attributes.zip_label);

    if (country === 'LI') {
      return {
        street: attributes.stn_label,
        streetNumber: attributes.adr_number,
        ...locality,
        country: 'LI',
        placeId: attributes.adr_egaid.toString(),
        coordinates: suggestion.coordinates,
      };
    }

    const canton = suggestion.canton;
    if (!canton) {
      throw new Error('GeoAdmin returned a Swiss address without a canton.');
    }
    if (allowedCantons && !allowedCantons.includes(canton)) {
      throw new Error('The selected address is outside the allowed cantons.');
    }

    return {
      street: attributes.stn_label,
      streetNumber: attributes.adr_number,
      ...locality,
      canton,
      country: 'CH',
      placeId: attributes.adr_egaid.toString(),
      coordinates: suggestion.coordinates,
      communeBfs: attributes.com_fosnr,
    };
  };
}

/** GeoAdmin autocomplete for Switzerland and Liechtenstein, plus manual entry. */
const SwissAddressInput = React.forwardRef<
  HTMLInputElement,
  SwissAddressInputProps
>(
  (
    {
      name,
      defaultValue,
      onChange,
      label,
      helperText,
      error = false,
      errors,
      placeholder,
      noOptionsText,
      searchingText,
      labels: labelOverrides,
      placeholders: placeholderOverrides,
      disabled = false,
      required = false,
      id,
      className,
      allowedCantons,
      onFetchError,
      disablePortal = false,
      defaultManual = false,
    },
    ref,
  ) => {
    const initialConfig = React.useRef({
      defaultValue,
      onChange,
      onFetchError,
      allowedCantons,
    }).current;
    const [initialValue] = React.useState(() =>
      initialConfig.defaultValue
        ? toBaseValue(initialConfig.defaultValue)
        : null,
    );
    const [value, setValue] = React.useState<SwissBaseAddressValue | null>(
      initialValue,
    );
    const [resolvingDefault, setResolvingDefault] = React.useState(false);
    const defaultResolutionRef = React.useRef<AbortController | null>(null);
    const country = value?.country ?? 'CH';
    const provider = React.useMemo(
      () => createSwissAddressSuggestionsProvider(allowedCantons, country),
      [allowedCantons, country],
    );
    const resolver = React.useMemo(
      () => createSwissAddressDetailsResolver(allowedCantons, country),
      [allowedCantons, country],
    );

    React.useEffect(() => {
      const isVerified = isVerifiedAddress(initialValue);
      initialConfig.onChange?.(
        isVerified && initialValue ? toSwissValue(initialValue) : null,
      );
    }, [initialConfig, initialValue]);

    React.useEffect(() => {
      const initialDefaultValue = initialConfig.defaultValue;
      if (!initialDefaultValue || !initialValue) return;
      if (isVerifiedAddress(initialValue)) {
        return;
      }

      const controller = new AbortController();
      defaultResolutionRef.current = controller;
      const initialProvider = createSwissAddressSuggestionsProvider(
        initialConfig.allowedCantons,
        initialValue.country,
      );
      const initialResolver = createSwissAddressDetailsResolver(
        initialConfig.allowedCantons,
        initialValue.country,
      );
      const enrichDefault = async () => {
        setResolvingDefault(true);
        try {
          const suggestions = await initialProvider(
            formatDefaultValue(initialDefaultValue),
            { signal: controller.signal },
          );
          const first = suggestions[0];
          if (!first) return;
          const suggestion = initialDefaultValue.featureId
            ? { ...first, id: initialDefaultValue.featureId }
            : first;
          const resolved = await initialResolver(suggestion, {
            signal: controller.signal,
          });
          if (controller.signal.aborted) return;
          setValue(resolved);
          initialConfig.onChange?.(toSwissValue(resolved));
        } catch (fetchError) {
          if (!isAbortError(fetchError, controller.signal)) {
            initialConfig.onFetchError?.(fetchError);
          }
        } finally {
          if (!controller.signal.aborted) setResolvingDefault(false);
        }
      };
      void enrichDefault();
      return () => controller.abort();
    }, [initialConfig, initialValue]);

    const cancelDefaultResolution = React.useCallback(() => {
      defaultResolutionRef.current?.abort();
      defaultResolutionRef.current = null;
      setResolvingDefault(false);
    }, []);

    const mergedLabels = {
      ...SWISS_LABELS,
      ...labelOverrides,
      ...(label ? { search: label } : {}),
      ...(noOptionsText ? { noOptions: noOptionsText } : {}),
      ...(searchingText ? { searching: searchingText } : {}),
    };
    const mergedPlaceholders = {
      ...SWISS_PLACEHOLDERS,
      ...placeholderOverrides,
      ...(placeholder ? { search: placeholder } : {}),
    };
    const mergedErrors: AddressInputFieldErrors = {
      ...(error
        ? {
            search: true,
            street: true,
            streetNumber: true,
            postalCode: true,
            city: true,
            canton: true,
          }
        : {}),
      ...errors,
    };
    const cantonOptions = (allowedCantons ?? SWISS_CANTONS).map((canton) => ({
      value: canton,
      label: canton,
    }));

    return (
      <AddressAutocompleteInput<SwissBaseAddressValue, SwissAddressSuggestion>
        ref={ref}
        id={id}
        name={name}
        value={value}
        onChange={(nextValue) => {
          setValue(nextValue);
          onChange?.(nextValue ? toSwissValue(nextValue) : null);
        }}
        suggestionsProvider={provider}
        detailsResolver={resolver}
        debounceMs={500}
        minimumQueryLength={3}
        defaultCountry="CH"
        allowedCountries={['CH', 'LI']}
        autocompleteCountries={['CH', 'LI']}
        defaultManual={defaultManual}
        labels={mergedLabels}
        placeholders={mergedPlaceholders}
        errors={mergedErrors}
        helperText={helperText}
        disabled={disabled}
        required={required}
        className={className}
        onFetchError={onFetchError}
        onEdit={cancelDefaultResolution}
        disablePortal={disablePortal}
        resolving={resolvingDefault}
        renderRegionField={(context) => {
          const {
            value: manualValue,
            updateValue,
            disabled: fieldsDisabled,
            isRequired,
            disablePortal: inline,
            baseId,
            name: fieldName,
          } = context;
          if (manualValue.country !== 'CH') return null;
          return (
            <div className="grid gap-1.5">
              <Label
                htmlFor={`${baseId}-canton`}
                required={isRequired('canton')}
              >
                {mergedLabels.canton}
              </Label>
              <Select
                id={`${baseId}-canton`}
                options={cantonOptions}
                value={manualValue.canton}
                onChange={(canton) =>
                  updateValue({ canton: canton as SwissCanton })
                }
                placeholder={mergedLabels.cantonPlaceholder}
                noResultsText={mergedLabels.noCantons}
                disabled={fieldsDisabled}
                error={Boolean(mergedErrors.canton)}
                disablePortal={inline}
              />
              {fieldName && (
                <input
                  type="hidden"
                  name={`${fieldName}.canton`}
                  value={manualValue.canton ?? ''}
                  disabled={fieldsDisabled}
                />
              )}
              {typeof mergedErrors.canton === 'string' && (
                <p className="text-sm text-destructive">
                  {mergedErrors.canton}
                </p>
              )}
            </div>
          );
        }}
      />
    );
  },
);

SwissAddressInput.displayName = 'SwissAddressInput';

export {
  SWISS_CANTONS,
  SwissAddressInput,
  createSwissAddressDetailsResolver,
  createSwissAddressSuggestionsProvider,
  isLiechtensteinCommune,
  zGeoAdminFeature,
  zGeoAdminResults,
};
